import { useEffect, useState, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Stack } from '@mui/material';
import clsx from 'clsx';
import { Filter, Table } from '../../../packages/ui/organisms/table';
import {
  Badge,
  badgeSize,
  badgeStyle,
  badgeType,
  colors,
  fontWeight,
  Heading,
  headingLevel,
  Icons,
  Menu,
  Text,
} from '../../../packages';
import { ProfileInfo } from '../../../packages/ui/templates/profile-info';
import { DROPDOWN_FILTER_COLLAPSABLE_TYPE } from '../../../packages/ui/organisms/table/filter';
import dayjs from '../../../utils/dayjs';
import { sentenceCase } from 'sentence-case';

import { reportingActionCreators } from '../../../redux/modules/reporting';
import { selectInvoice, selectInvoiceList } from '../../../redux/modules/reporting/selectors';

import { BaseLayout } from '../../../layouts/base';
import { profileActionCreators } from '../../../redux/modules/profile';
import { saveAs } from 'file-saver';

import InvoiceDetailDrawer from './invoices-detail-drawer';
import {
  DATE_FILTER_TYPES,
  INVOICE_FAST_FILTERS,
  INVOICE_TYPES,
  INVOICE_TYPE_TABS,
  STATUS_TABS,
  multiSelectFilterOptionsMock,
  tableColumns,
} from './Invoices.constants';
import { useStyles } from './Invoices.styles';
import EditInvoice from '../claim-detail/components/EditInvoice';
import { getInvoiceCountsByStatus, getInvoices } from '../../../services/reporting/reporting.service';
import { showSnackbar } from '../../../redux/modules/snackbar';
import { Tabs } from '../../../packages/ui/atoms/tabs';

const invoiceStatuses = ['PAID', 'ON_HOLD', 'CANCELLED', 'PENDING', 'WRITE_OFF', 'PAYMENT_FAILED'];

const InvoiceList = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [tableParams, setTableParams] = useState({
    search: {
      searchKey: '',
      filter: [],
      dateFilter: {
        startDate: '',
        endDate: '',
      },
    },
    pagination: { currentPage: 1, rowsPerPage: 10 },
    sorter: { direction: 'desc', column: '' },
  });
  const [filterChangeLoading, setFilterChangeLoading] = useState(false);
  const [resetMultiSelectFilter, setResetMultiSelectFilter] = useState(false);
  const [filterOptionsMock, setFilterOptionsMock] = useState([]);
  const [selectedFilter] = useState('All');
  const [selectedItem, setSelectedItem] = useState(null);
  const [invoiceList, setInvoiceList] = useState([]);
  const providersList = useSelector(state => state.profile.providers2);
  const { isLoading, invoicesList = [], additionalMetaData, totalRecords } = useSelector(selectInvoiceList);
  const [openInvoice, setOpenInvoice] = useState(false);
  const [openInvoiceDetailDrawer, setOpenInvoiceDetailDrawer] = useState(false);
  const [isLoadingExport, setIsLoadingExport] = useState(false);
  const [tabType, setTabType] = useState(INVOICE_FAST_FILTERS.ALL);
  const tabsContainerRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [tabs, setTabs] = useState([
    { tabKey: INVOICE_FAST_FILTERS.ALL, label: 'All' },
    { tabKey: INVOICE_FAST_FILTERS.NEW, label: 'New' },
    {
      tabKey: INVOICE_FAST_FILTERS.PAID,
      label: 'Paid',
      count: 0,
    },
    {
      tabKey: INVOICE_FAST_FILTERS.ON_HOLD,
      label: 'On Hold',
      count: 0,
    },
    {
      tabKey: INVOICE_FAST_FILTERS.WRITE_OFF,
      label: 'Write Off',
      count: 0,
    },
    {
      tabKey: INVOICE_FAST_FILTERS.PAYMENT_FAILED,
      label: 'Payment Failed',
      count: 0,
    },
    {
      tabKey: INVOICE_FAST_FILTERS.PATIENT_RESP,
      label: 'Patient Responsibility',
      count: 0,
    },
    {
      tabKey: INVOICE_FAST_FILTERS.NO_SHOW,
      label: 'No show',
      count: 0,
    },
    {
      tabKey: INVOICE_FAST_FILTERS.ELIGIBLE_INSURANCE,
      label: 'Eligible',
      count: 0,
    },
    {
      tabKey: INVOICE_FAST_FILTERS.MEDICAID,
      label: 'Medicaid',
      count: 0,
    },
    {
      tabKey: INVOICE_FAST_FILTERS.COMMERCIAL,
      label: 'Commercial',
      count: 0,
    },
    {
      tabKey: INVOICE_FAST_FILTERS.CASH_PAYMENT,
      label: 'Cash',
      count: 0,
    },
    {
      tabKey: INVOICE_FAST_FILTERS.VERIFICATION_FAILED,
      label: 'VOB Failed',
      count: 0,
    },
  ]);
  const currentInvoiceReduxState = useSelector(selectInvoice);

  const onExportClick = () => {
    const multiselectFilters = tableParams.search?.multiSelectFilter;
    const statuses = getMultiSelectFilterForStatus();
    const states = multiselectFilters?.State ?? [];
    const roles = multiselectFilters?.Role ?? [];

    const selectedProviders = multiselectFilters?.[DROPDOWN_FILTER_COLLAPSABLE_TYPE.PROVIDERS]?.map(
      provider => {
        return provider.id;
      },
    );

    setIsLoadingExport(true);

    const params = {
      pageNumber: 1,
      pageSize: totalRecords,
      sortBy: 'createdAt',
      sortOrder: tableParams?.sorter.direction,
      exportInvoice: true,
    };

    const body = {
      searchQuery: tableParams?.search.searchKey,
      startDate: tableParams?.search.dateFilter?.startDate
        ? dayjs(tableParams?.search.dateFilter?.startDate)?.toISOString()
        : null,
      endDate: tableParams?.search.dateFilter?.endDate
        ? dayjs(tableParams?.search.dateFilter?.endDate)?.toISOString()
        : null,
      statuses,
      states,
      roles,
      providerIds: selectedProviders,
    };
    getInvoices(body, params, true)
      .then(res => {
        if (res.status === 200) {
          const blob = new Blob([res.data], {
            type: 'application/vnd.ms-excel',
          });
          saveAs(blob, 'Invoices list.xlsx');
          setIsLoadingExport(false);
          dispatch(
            showSnackbar({
              snackType: 'success',
              snackMessage: 'Invoices exported successfully',
            }),
          );
        }
      })
      .catch(err => {
        setIsLoadingExport(false);
        dispatch(
          showSnackbar({
            snackType: 'error',
            snackMessage: err?.data?.errors?.[0]?.endUserMessage || 'Error exporting invoices',
          }),
        );
      });
  };

  const getMultiSelectFilterForStatus = () => {
    const multiselectFilters = tableParams.search?.multiSelectFilter || {};
    const statuses = [];
    if (multiselectFilters.Status) {
      multiselectFilters.Status.forEach(filter => {
        switch (filter) {
          case 'Paid':
            statuses.push('PAID');
            break;
          case 'On Hold':
            statuses.push('ON_HOLD');
            break;
          case 'Write Off':
            statuses.push('WRITE_OFF');
            break;
          case 'Payment Failed':
            statuses.push('PAYMENT_FAILED');
            break;
          case 'New':
            statuses.push('NEW');
            break;
          default:
            statuses.push(filter.toUpperCase().replaceAll(' ', '_'));
            break;
        }
      });
    } else {
      // Only add tabType to statuses if it's a status tab, not an invoice type tab
      if (tabType && tabType !== INVOICE_FAST_FILTERS.ALL && STATUS_TABS.includes(tabType)) {
        statuses.push(tabType);
      }
    }

    return statuses;
  };

  const handleFetchInvoicesFormat = tableParam => {
    const multiselectFilters = tableParam.search?.multiSelectFilter;
    const states = multiselectFilters?.State ?? [];
    const roles = multiselectFilters?.Role ?? [];
    const selectedProviders = multiselectFilters?.[DROPDOWN_FILTER_COLLAPSABLE_TYPE.PROVIDERS]?.map(
      provider => provider.id,
    );

    // Determine if current tab is an invoice type tab
    const isInvoiceTypeTab = INVOICE_TYPE_TABS.includes(tabType);

    // Get invoice type: prioritize manual filter selection, then tab selection (same pattern as status tabs)
    let invoiceType = '';
    if (multiselectFilters?.['Invoice Type']?.[0]) {
      invoiceType = INVOICE_TYPES[multiselectFilters['Invoice Type'][0]] || '';
    } else if (isInvoiceTypeTab && tabType !== INVOICE_FAST_FILTERS.ALL) {
      invoiceType = tabType || '';
    }

    const statuses = getMultiSelectFilterForStatus();

    return {
      pageSize: tableParam?.pagination.rowsPerPage,
      pageNumber: tableParam?.pagination.currentPage,
      sortBy: 'createdAt',
      sortOrder: tableParam?.sorter.direction,
      body: {
        searchQuery: tableParam?.search.searchKey,
        startDate: tableParam?.search.dateFilter?.startDate
          ? dayjs(tableParam?.search.dateFilter?.startDate)?.toISOString()
          : null,
        endDate: tableParam?.search.dateFilter?.endDate
          ? dayjs(tableParam?.search.dateFilter?.endDate)?.toISOString()
          : null,
        statuses,
        states,
        roles,
        providerIds: selectedProviders,
        dateFilterType:
          DATE_FILTER_TYPES[multiselectFilters?.['Apply Dates Filter on']?.[0] || 'Session Date'],
        invoiceType,
      },
    };
  };

  const onRowClick = data => {
    setSelectedItem(data);
    setOpenInvoiceDetailDrawer(true);
  };

  const checkScrollability = useCallback(() => {
    const container = tabsContainerRef.current?.querySelector('.MuiTabs-flexContainer');
    if (container) {
      const { scrollLeft, scrollWidth, clientWidth } = container;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
    }
  }, []);

  const scrollTabs = useCallback(direction => {
    const container = tabsContainerRef.current?.querySelector('.MuiTabs-flexContainer');
    if (container) {
      const scrollAmount = 200;
      container.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth',
      });
    }
  }, []);

  useEffect(() => {
    checkScrollability();
    const container = tabsContainerRef.current?.querySelector('.MuiTabs-flexContainer');
    if (container) {
      container.addEventListener('scroll', checkScrollability);
      window.addEventListener('resize', checkScrollability);
      return () => {
        container.removeEventListener('scroll', checkScrollability);
        window.removeEventListener('resize', checkScrollability);
      };
    }
  }, [checkScrollability, tabs]);

  useEffect(() => {
    dispatch(reportingActionCreators.fetchInvoices(handleFetchInvoicesFormat(tableParams)));
  }, [tableParams, selectedFilter]);

  useEffect(() => {
    const formatInvoices = invoicesList?.map(invoice => ({
      ...invoice,
      totalAmount: Number.isInteger(invoice?.totalAmount)
        ? invoice?.totalAmount
        : `${invoice?.totalAmount?.toFixed(2) || 0}`,
      actions: {
        ...invoice,
        totalAmount: Number.isInteger(invoice?.totalAmount)
          ? invoice?.totalAmount
          : `${invoice?.totalAmount?.toFixed(2) || 0}`,
      },
    }));

    setInvoiceList(formatInvoices);
  }, [invoicesList]);

  const updateInvoiceStatus = (data, updatedStatus) => {
    dispatch(
      reportingActionCreators.updateInvoiceStatusById({
        requestBody: { status: updatedStatus },
        id: data?._id,
        filters: handleFetchInvoicesFormat(tableParams),
      }),
    );
  };

  /**
   * Generates a list of items for updating an invoice status, based on the current status of the invoice.
   * @param data The invoice record for which to generate the status options
   * @returns An array of items, each representing an available status option
   */
  const generateStatusOptions = data => {
    const items = invoiceStatuses
      .map(status => {
        if (data?.status === status || data?.status === 'PAID') {
          return null;
        }
        return {
          label: `Mark as ${status?.replaceAll('_', ' ')?.toLocaleLowerCase()}`,
          onClick: () => updateInvoiceStatus(data, status),
        };
      })
      .filter(Boolean);

    return items;
  };

  const renderColumns = () => {
    return tableColumns.map(column => {
      if (column.id === 'invoiceDate') {
        return {
          ...column,
          renderCell: invoiceDate => (
            <div className={classes.timeWrap}>
              <div className={classes.date}>{dayjs(invoiceDate).format('MM/DD/YYYY')}</div>
              <div className={classes.time}>{dayjs(invoiceDate).format('h:mm A')}</div>
            </div>
          ),
        };
      }
      if (column.id === 'member') {
        return {
          ...column,
          renderCell: ({ firstName, lastName, fullName, profileImage, id, uuid }) => (
            <ProfileInfo
              type="member"
              photo={profileImage}
              nickName={
                firstName && lastName ? `${firstName || ''} ${lastName || ''}` : fullName ?? 'No Name'
              }
              fullName={uuid}
              memberId={id}
            />
          ),
        };
      }
      if (column.id === 'provider') {
        return {
          ...column,
          renderCell: ({ firstName, lastName, profileImage, designation, id }) => (
            <ProfileInfo
              type="provider"
              photo={profileImage}
              role={designation}
              fullName={`${firstName} ${lastName}`}
              memberId={id}
            />
          ),
        };
      }
      /* TO DO
        Change totalAmount based on amount rendering: styleguide is done.
      */
      // if (column.id === 'amount') {
      //   return {
      //     ...column,
      //     renderCell: ({ amountPaid, increased, changed }) => (
      //       <Box display="flex">
      //         {changed ? (
      //           <Box display="flex" justifyContent="center" alignItems="center">
      //             <Badge
      //               variant={badgeType.OUTLINED}
      //               style={increased ? badgeStyle.RESOLVED : badgeStyle.HIGH}
      //               className={clsx(classes.badge, { [classes.negativeBadge]: !increased })}
      //             >
      //               <Icons
      //                 glyph="arrow-left"
      //                 className={clsx(increased ? classes.increment : classes.incrementNegative)}
      //               />
      //             </Badge>
      //             <Box marginLeft={1}>${amountPaid}</Box>
      //           </Box>
      //         ) : (
      //           <span>${amountPaid}</span>
      //         )}
      //       </Box>
      //     ),
      //   };
      // }
      if (column.id === 'totalAmount') {
        return {
          ...column,
          renderCell: val => {
            return <Box>${val}</Box>;
          },
        };
      }
      if (column.id === 'state') {
        return {
          ...column,
          renderCell: state => {
            return <Box>{state?.name || 'N/A'}</Box>;
          },
        };
      }
      if (column.id === 'status') {
        return {
          ...column,
          renderCell: val => {
            return (
              <Badge
                variant={badgeType.OUTLINED}
                style={
                  val === 'PAID'
                    ? badgeStyle.RESOLVED
                    : val === 'PENDING' || val === 'COMPLETED' || val === 'NEW'
                    ? badgeStyle.PRIMARY
                    : badgeStyle.HIGH
                }
                className={clsx(
                  classes.status,
                  { [classes.unpaidStatus]: val !== 'PAID' },
                  { [classes.submittedStatus]: val === 'PENDING' || val === 'COMPLETED' || val === 'NEW' },
                )}
              >
                {sentenceCase(val || '')}
              </Badge>
            );
          },
        };
      }
      if (column.id === 'dueDate') {
        return {
          ...column,
          renderCell: dueDate => (
            <div className={classes.timeWrap}>
              <div className={classes.date}>{dayjs(dueDate).format('MM/DD/YYYY')}</div>
              <div className={classes.time}>{dayjs(dueDate).format('h:mm A')}</div>
            </div>
          ),
        };
      }
      if (column.id === 'sessionDate') {
        return {
          ...column,
          renderCell: sessionDate => (
            <div className={classes.timeWrap}>
              <div className={classes.date}>{dayjs(sessionDate).format('MM/DD/YYYY')}</div>
              <div className={classes.time}>{dayjs(sessionDate).format('h:mm A')}</div>
            </div>
          ),
        };
      }
      // TODO: Will implement daysPastDue in future ticket

      // if (column.id === 'daysPastDue') {
      //   return {
      //     ...column,
      //     renderCell: (dueDate) => (
      //       <div className={classes.timeWrap}>
      //         <div className={classes.date}>{dayjs(dueDate).format('MMMM D, YYYY')}</div>
      //         <div className={classes.time}>{dayjs(dueDate).format('h:mm A')}</div>
      //       </div>
      //     ),
      //   };
      // }
      // if (column.id === 'sessionType') {
      //   return {
      //     ...column,
      //     renderCell: (sessionType) => (
      //       <div className={classes.timeWrap}>
      //         <div className={classes.date}>{sessionType || 'N/A'}</div>
      //       </div>
      //     ),
      //   };
      // }
      if (column.id === 'reason') {
        return {
          ...column,
          renderCell: reason => (
            <div className={classes.timeWrap}>
              <div className={classes.reasonText}>{reason || 'N/A'}</div>
            </div>
          ),
        };
      }
      if (column.id === 'actions') {
        return {
          ...column,
          renderCell: data => (
            <Menu
              icon="more"
              className={classes.menu}
              itemsWrapperClassName={classes.menuItemsWrapper}
              items={[
                {
                  label: 'View Details',
                  onClick: () => {
                    setSelectedItem(data);
                    setOpenInvoiceDetailDrawer(true);
                  },
                },
                ...generateStatusOptions(data),
                {
                  label: 'Request payment',
                  onClick: () => {
                    updateInvoiceStatus(data, 'PENDING');
                  },
                },
                data?.status !== 'PAID' && {
                  label: 'Edit Invoice',
                  onClick: () => {
                    setOpenInvoiceDetailDrawer(false);
                    dispatch(reportingActionCreators.fetchInvoiceById({ id: data?._id }));
                    setTimeout(() => {
                      setSelectedItem(data);
                      setOpenInvoice(true);
                    }, 1000);
                  },
                },
              ].filter(Boolean)}
            />
          ),
        };
      }
      return column;
    });
  };

  const onCloseDrawer = () => {
    setSelectedItem(null);
    setOpenInvoiceDetailDrawer(false);
  };
  const renderStatusTotal = (status, value, badge) => (
    <Box
      padding={2}
      sx={{
        background: colors.white,
        flex: '1 1 0',
        boxShadow:
          '0px 10px 20px rgba(0, 0, 0, 0.04), 0px 2px 6px rgba(0, 0, 0, 0.04), 0px 0px 1px rgba(0, 0, 0, 0.04)',
        borderRadius: '8px',
      }}
    >
      <Box display="flex" justifyContent="space-between">
        <Text className={classes.label}>{status.toUpperCase()}</Text>
        <Badge
          className={clsx(
            classes.statusLabel,
            { [classes.unpaidStatus]: badge === 'Write Off' },
            { [classes.submittedStatus]: badge === 'Pending' || badge === 'New' },
          )}
          variant={badgeType.OUTLINED}
          style={
            badge === 'Paid'
              ? badgeStyle.RESOLVED
              : badge === 'Pending' || badge === 'New'
              ? badgeStyle.PRIMARY
              : badgeStyle.HIGH
          }
        >
          {badge}
        </Badge>
      </Box>
      <Text className={classes.statusFee}>
        {new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        }).format(value)}
      </Text>
    </Box>
  );

  const renderTab = tab => {
    return {
      tabKey: tab.tabKey,
      label: tab.label || tab.tabKey,
      icon: (
        <Badge className={classes.countBadge} size={badgeSize.XSMALL}>
          {tab?.count ?? 0}
        </Badge>
      ),
      iconPosition: 'end',
    };
  };

  const fetchCounts = async () => {
    const res = await getInvoiceCountsByStatus();
    const counts = res?.data?.data || [];
    const totalCountObj = counts.find(count => count._id === 'TOTAL');
    const total = totalCountObj?.count || 0;
    const updatedTabs = tabs.map(tab => {
      if (tab.tabKey === INVOICE_FAST_FILTERS.ALL) {
        return {
          ...tab,
          count: total,
        };
      }
      const countObj = counts.find(count => count._id === tab.tabKey);
      return {
        ...tab,
        count: countObj ? countObj.count : 0,
      };
    });
    setTabs(updatedTabs);
  };

  useEffect(() => {
    setFilterOptionsMock(multiSelectFilterOptionsMock(providersList));
    setResetMultiSelectFilter(true);
  }, [providersList]);

  useEffect(() => {
    setResetMultiSelectFilter(false);
  }, [tableParams]);

  useEffect(() => {
    setFilterChangeLoading(true);
    setTimeout(() => {
      setFilterChangeLoading(false);
    }, 100);
  }, [filterOptionsMock]);

  useEffect(() => {
    if (providersList?.length <= 0) dispatch(profileActionCreators.fetchAllProviders());
    void fetchCounts();
  }, []);

  return (
    <BaseLayout>
      {openInvoiceDetailDrawer && (
        <InvoiceDetailDrawer
          open={openInvoiceDetailDrawer}
          onClose={onCloseDrawer}
          invoice={selectedItem}
          updateInvoiceStatus={(data, status) => updateInvoiceStatus(data, status)}
        />
      )}
      <Box className={classes.root}>
        <Stack direction="row" justifyContent="space-between" spacing={2} sx={{ marginBottom: 5 }}>
          <Stack direction="row" alignItems="center" gap={2} sx={{ height: 48 }}>
            <Heading className={classes.heading} level={headingLevel.XL} weight={fontWeight.BOLD}>
              Invoices
            </Heading>
            <Badge className={classes.totalBadge} variant={badgeType.OUTLINED} style={badgeStyle.UNRELATED}>
              {totalRecords || '0'} total
            </Badge>
          </Stack>
        </Stack>
        {openInvoice && !currentInvoiceReduxState?.errorMsg && currentInvoiceReduxState?.data && (
          <EditInvoice
            open={openInvoice}
            onClose={() => setOpenInvoice(false)}
            data={currentInvoiceReduxState?.data || selectedItem}
          />
        )}
        <Box display="flex" flexDirection="column" mb={4} gap={0}>
          <Box display="flex" mb={2} gap={2}>
            {renderStatusTotal('Total Pending', additionalMetaData?.totalPending || 0, 'Pending')}
            {renderStatusTotal('Total New', additionalMetaData?.totalNew || 0, 'New')}
            {renderStatusTotal('Total Paid', additionalMetaData?.totalPaid || 0, 'Paid')}
            {renderStatusTotal('Total Write off', additionalMetaData?.totalWriteOff || 0, 'Write Off')}
          </Box>
        </Box>
        <Box
          sx={{ display: 'flex', alignItems: 'center', gap: 1, backgroundColor: 'white' }}
          ref={tabsContainerRef}
        >
          {canScrollLeft && (
            <Box onClick={() => scrollTabs('left')} className={classes.scrollArrowButton}>
              <Icons
                glyph="caret-down"
                color={colors.neutral600}
                className={clsx(classes.scrollArrow, classes.scrollArrowLeft)}
              />
            </Box>
          )}
          <Box sx={{ flex: 1, overflow: 'hidden' }}>
            <Tabs
              value={tabType}
              onChange={value => {
                setTabType(value);
                setTableParams({
                  ...tableParams,
                  pagination: { ...tableParams.pagination, currentPage: 1 },
                  search: {
                    ...tableParams.search,
                    multiSelectFilter: {
                      ...tableParams.search?.multiSelectFilter,
                    },
                  },
                });
              }}
              options={tabs.map(renderTab)}
              className={classes.tabs}
            />
          </Box>
          {canScrollRight && (
            <Box onClick={() => scrollTabs('right')} className={classes.scrollArrowButton}>
              <Icons
                glyph="caret-down"
                color={colors.neutral600}
                className={clsx(classes.scrollArrow, classes.scrollArrowRight)}
              />
            </Box>
          )}
        </Box>
        <div className={classes.invoiceList}>
          {filterChangeLoading ? (
            <div className={classes.loader}>
              <Icons className="rotate linear infinite" glyph="in-progress" color={colors.primary} />
              Loading...
            </div>
          ) : (
            <Table
              searchProps={{
                placeholder: 'Search invoices by member and provider',
                filterProps: {
                  variant: Filter.tableFilterType.MULTIPLE,
                  multiSelectOptions: multiSelectFilterOptionsMock(providersList),
                  dateFilter: {
                    startDate: tableParams?.search?.dateFilter?.startDate,
                    endDate: tableParams?.search?.dateFilter?.endDate,
                  },
                },
                resetMultiSelectFilter,
                exportProps: {
                  btnTitle: 'Export',
                  isLoadingExport,
                },
              }}
              onClickExport={onExportClick}
              gridProps={{
                columns: renderColumns(),
                data: invoiceList || [],
                isLoading,
                onRowClick,
              }}
              paginationProps={{
                currentRows: invoiceList?.length || 0,
                totalCount: totalRecords || 0,
                showRowsPerPage: true,
              }}
              value={tableParams}
              onChange={newTableParams => {
                setTableParams(newTableParams);
              }}
            />
          )}
        </div>
      </Box>
    </BaseLayout>
  );
};

export { InvoiceList };
